import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_MARKS } from "../brand/marks";
import { ALL_PRODUCTS } from "../nav/navigation";
import type { CtaLink, OutcomePageContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * Solution and industry template: challenge → outcomes → regulatory drivers →
 * the products that deliver it → Warden → resources → close. Products come
 * after the outcome deliberately; someone who arrived with a regulator in mind
 * should not have to learn the portfolio to find out whether we help.
 */
export function OutcomePage({ content }: { content: OutcomePageContent }) {
  useEffect(() => {
    document.title = `${content.headline} — Odingard`;
  }, [content.headline]);

  const products = content.products.slugs
    .map((slug) => ALL_PRODUCTS.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  return (
    <main className="page pdp outcome" id="main">
      <section className="pdp__hero">
        <p className="page__eyebrow">{content.eyebrow}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
        <div className="pdp__ctas">
          {content.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>

      <section className="pdp__section" id="challenge">
        <h2>{content.challenge.heading}</h2>
        <p className="pdp__body">{content.challenge.body}</p>
        <ol className="pdp__steps">
          {content.challenge.points.map((point) => (
            <li key={point.title}>
              <b>{point.title}</b>
              <span>{point.body}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="pdp__section" id="outcomes">
        <h2>{content.outcomes.heading}</h2>
        {content.outcomes.lede && <p className="pdp__body">{content.outcomes.lede}</p>}
        <div className="pdp__features">
          {content.outcomes.items.map((item) => (
            <article key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      {content.drivers && (
        <section className="pdp__section pdp__section--evidence" id="drivers">
          <h2>{content.drivers.heading}</h2>
          {content.drivers.lede && <p className="pdp__body">{content.drivers.lede}</p>}
          <dl className="outcome__drivers">
            {content.drivers.items.map((item) => (
              <div key={item.title}>
                <dt>{item.title}</dt>
                <dd>{item.body}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <section className="pdp__section" id="products">
        <h2>{content.products.heading}</h2>
        {content.products.lede && <p className="pdp__body">{content.products.lede}</p>}
        <div className="home__cards">
          {products.map((p) => {
            const Mark = PRODUCT_MARKS[p.mark];
            return (
              <Link key={p.slug} to={p.href} className="mm__card">
                <span className="mm__lockup" style={{ color: p.accent }}>
                  <Mark size={30} />
                  <b>{p.name}</b>
                </span>
                <span className="mm__descriptor">{p.descriptor}</span>
                <span className="mm__blurb">{p.blurb}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {content.services && (
        <section className="pdp__section pdp__section--warden" id="services">
          <p className="page__eyebrow">Warden · By Odingard</p>
          <h2>{content.services.heading}</h2>
          <p className="pdp__body">{content.services.body}</p>
          <ul className="pdp__links">
            {content.services.links.map((link) => (
              <li key={link.href}>
                <Link to={link.href}>{link.label} →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.resources && (
        <section className="pdp__section" id="resources">
          <h2>{content.resources.heading}</h2>
          <div className="pdp__resources">
            {content.resources.items.map((item) =>
              item.external ? (
                <a key={item.href} href={item.href}>
                  <span className="pdp__kind">{item.kind}</span>
                  <b>{item.label}</b>
                </a>
              ) : (
                <Link key={item.href} to={item.href}>
                  <span className="pdp__kind">{item.kind}</span>
                  <b>{item.label}</b>
                </Link>
              ),
            )}
          </div>
        </section>
      )}

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
